import { createReducer, on } from '@ngrx/store';
import { ActiveComponentChanged, ResetStateFromRouter } from 'src/app/shared/actions/eliqua.actions';
import { v4 as uuidv4 } from 'uuid';
import {
  SaveBrands,
  SaveClubs,
  SaveCouponSets,
  SaveStoreGroups,
  SaveUsers,
} from '../actions/add-store.actions';
import {
  AddAttributeRow,
  AddPOSRow,
  AddressLineChanged,
  AttributeBoolChanged,
  AttributeStringChanged,
  CloseSidenav as CloseChipsSidenav,
  CodeChanged,
  CoordinatesChanged,
  CountryChanged,
  CountryCodeChanged,
  CreateNew,
  DeactivateStoreChanged,
  DeleteAttributeRow,
  DeleteAttributeRowAll,
  DeletePOSRow,
  DeletePOSRowAll,
  DescriptionChanged,
  DisplayLanguageChanged,
  GetStoreSuccess,
  InitAttributeColumns,
  InitPOSColumns,
  LanguagesChanged,
  NameChanged,
  OpenSidenav,
  POSTableChanged,
  PostCodeChanged,
  RegionChanged,
  RegionValuesChanged,
  RemoveClub,
  RemoveClubs,
  RemoveCouponset,
  RemoveCouponsets,
  RemoveStoreBrand,
  RemoveStoreBrands,
  RemoveStoreGroup,
  RemoveStoreGroups,
  RemoveUser,
  RemoveUsers,
  SearchMerchantByIDSuccess,
  SelectedLanguageChanged,
  SetMerchantID,
  TownChanged,
  UpdateStore,
} from '../actions/edit-store.actions';
import { CloseSidenav, EditStore, GetCountriesSuccess } from '../actions/search.actions';
import {
  AttributesTableID,
  EditStoreState,
  POSTableID,
  TableStoreGroup,
} from '../models/store.model';

const initialState: EditStoreState = {
  sideNavOpened: false,
  isNew: true,
  code: '',
  countryCode: '',
  languages: [],
  selectedLanguage: '',
  storeNames: [],
  description: '',
  storeGroups: [],
  posTable: [],
  posDisplayedColumns: {},
  attributesTable: [],
  attributesDisplayedColumns: {},
  attributeTypes: ['STRING', 'INTEGER', 'BOOLEAN', 'DATE'],
  deactivateStore: false,
  clubs: [],
  brands: [],
  couponSets: [],
  users: [],
  addressDetails: {
    addressLine: '',
    town: '',
    postCode: '',
    countryValues: [],
    country: 0,
    countryCode: '',
    regionValues: [],
    region: 0,
    geoPoint: '',
  },
  fromMerchant: false,
  merchant: {
    merchantName: '',
    merchantID: '',
  },
  storeID: '',
  submit: false,
};

export const EditStoreReducer = createReducer(
  initialState,
  on(GetCountriesSuccess, (state, { res }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      countryValues: res,
    },
  })),
  on(OpenSidenav, (state) => ({
    ...state,
    sideNavOpened: true,
  })),
  on(CloseSidenav, CreateNew, (state) => ({
    ...initialState,
    posDisplayedColumns: state.posDisplayedColumns,
    attributesDisplayedColumns: state.attributesDisplayedColumns,
    addressDetails: {
      ...state.addressDetails,
      addressLine: '',
      town: '',
      postCode: '',
      country: 0,
      countryCode: '',
      regionValues: [],
      region: 0,
      geoPoint: '',
    },
    merchant: {
      merchantName: '',
      merchantID: '',
    },
    storeID: '',
  })),
  on(CloseChipsSidenav, (state) => ({
    ...state,
    sideNavOpened: false,
  })),
  on(InitPOSColumns, (state, { posDisplayedColumns }) => ({
    ...state,
    posDisplayedColumns,
  })),
  on(InitAttributeColumns, (state, { attributesDisplayedColumns }) => ({
    ...state,
    attributesDisplayedColumns,
  })),
  on(EditStore, (state, { store }) => ({
    ...state,
    isNew: false,
    code: store?.code ?? '',
    countryCode: store?.countryCode ?? '',
    storeNames: store?.storeNames ?? [],
    selectedLanguage: store?.selectedLanguageCode ?? '',
    description: store?.shortStoreDescription ?? '',
    deactivateStore: store?.deleteFlag ?? false,
    posTable: store?.storePosList?.map((storePos: any) => ({ ...storePos, id: uuidv4() })) ?? [],
    attributesTable:
      store?.attributes?.map((attribute: any) => ({ ...attribute, id: uuidv4() })) ?? [],
    storeGroups:
      store?.storeGroupList?.map((storeGroup: TableStoreGroup) => ({
        code: storeGroup.tableStoreGroupCode,
        id: storeGroup.tableStoreGroupId,
      })) ?? [],
    clubs: store?.storeClubs ?? [],
    brands: store?.brandsForStore ?? [],
    couponSets: store?.couponSets ?? [],
    users: store?.userList ?? [],
    addressDetails: {
      ...state.addressDetails,
      addressLine: store?.address?.addressLine1
        ? store?.address?.addressLine1
        : store?.address?.addressLine2
          ? store?.address?.addressLine2
          : store?.address?.addressLine3
            ? store?.address?.addressLine3
            : '',
      town: store?.address?.town ?? '',
      postCode: store?.address?.postCode ?? '',
      country: store?.address?.tableCountryId ?? 0,
      region: store?.address?.tableRegionId ?? 0,
      geoPoint:
        store?.address?.longitude?.toFixed && store?.address?.latitude?.toFixed
          ? `${store.address.latitude.toFixed(15)}, ${store.address.longitude.toFixed(15)}`
          : '',
    },
  })),
  on(GetStoreSuccess, (state, { res }) => ({
    ...state,
    isNew: false,
    code: res[0]?.code ?? '',
    countryCode: res[0]?.countryCode ?? '',
    storeNames: res[0]?.storeNames ?? [],
    selectedLanguage: res[0]?.selectedLanguageCode ?? '',
    description: res[0]?.shortStoreDescription ?? '',
    deactivateStore: res[0]?.deleteFlag ?? false,
    posTable: res[0]?.storePosList?.map((storePos: any) => ({ ...storePos, id: uuidv4() })) ?? [],
    attributesTable:
      res[0]?.attributes?.map((attribute: any) => ({ ...attribute, id: uuidv4() })) ?? [],
    storeGroups:
      res[0]?.storeGroupList?.map((storeGroup: TableStoreGroup) => ({
        code: storeGroup.tableStoreGroupCode,
        id: storeGroup.tableStoreGroupId,
      })) ?? [],
    clubs: res[0]?.storeClubs ?? [],
    brands: res[0]?.brandsForStore ?? [],
    couponSets: res[0]?.couponSets ?? [],
    users: res[0]?.userList ?? [],
    addressDetails: {
      ...state.addressDetails,
      addressLine: res[0]?.address?.addressLine1
        ? res[0]?.address?.addressLine1
        : res[0]?.address?.addressLine2
          ? res[0]?.address?.addressLine2
          : res[0]?.address?.addressLine3
            ? res[0]?.address?.addressLine3
            : '',
      town: res[0]?.address?.town ?? '',
      postCode: res[0]?.address?.postCode ?? '',
      country: res[0]?.address?.tableCountryId ?? 0,
      region: res[0]?.address?.tableRegionId ?? 0,
      geoPoint:
        res[0]?.address?.longitude?.toFixed && res[0]?.address?.latitude?.toFixed
          ? `${res[0].address.latitude.toFixed(15)}, ${res[0].address.longitude.toFixed(15)}`
          : '',
    },
    storeID: res[0]?.id,
  })),
  on(CodeChanged, (state, { code }) => ({
    ...state,
    code,
  })),
  on(CountryCodeChanged, (state, { countryCode }) => ({
    ...state,
    countryCode,
  })),
  on(SelectedLanguageChanged, (state, { selectedLanguage }) => ({
    ...state,
    selectedLanguage,
  })),
  on(NameChanged, (state, { name }) => ({
    ...state,
    storeNames: state.storeNames.map((store) =>
      store.languageCode === state.selectedLanguage ? { ...store, name } : store,
    ),
  })),
  on(DescriptionChanged, (state, { description }) => ({
    ...state,
    description,
  })),
  on(AddPOSRow, (state) => {
    let posTableCopy = [...state.posTable];
    posTableCopy.push({
      multiplier: 1,
      posNumber: null,
      vatNumber: '',
      id: uuidv4(),
    });
    return {
      ...state,
      posTable: posTableCopy,
    };
  }),
  on(POSTableChanged, (state, { posData, index, column }) => {
    let posTableCopy: POSTableID[] = [...state.posTable];
    posTableCopy[index] = { ...posTableCopy[index], [column]: posData };
    return {
      ...state,
      posTable: posTableCopy,
    };
  }),
  on(DeletePOSRow, (state, { id }) => ({
    ...state,
    posTable: state.posTable.filter((pos) => pos.id !== id),
  })),
  on(DeletePOSRowAll, (state) => ({
    ...state,
    posTable: [],
  })),
  on(AddAttributeRow, (state) => {
    let attributesTableCopy = [...state.attributesTable];
    attributesTableCopy.push({
      attributeName: '',
      attributeValue: '',
      dataType: null,
      id: uuidv4(),
    });
    return {
      ...state,
      attributesTable: attributesTableCopy,
    };
  }),
  on(DeleteAttributeRow, (state, { id }) => ({
    ...state,
    attributesTable: state.attributesTable.filter((attribute) => attribute.id !== id),
  })),
  on(DeleteAttributeRowAll, (state) => ({
    ...state,
    attributesTable: [],
  })),
  on(AttributeStringChanged, (state, { text, index, column }) => {
    let attributesTableCopy: AttributesTableID[] = [...state.attributesTable];
    attributesTableCopy[index] = {
      ...attributesTableCopy[index],
      [column]: text,
    };
    if (column === 'dataType') {
      attributesTableCopy[index].attributeValue = text === 'BOOLEAN' ? false : '';
      attributesTableCopy[index].attributeName = '';
    }
    return {
      ...state,
      attributesTable: attributesTableCopy,
    };
  }),
  on(AttributeBoolChanged, (state, { index }) => {
    let attributesTableCopy: AttributesTableID[] = [...state.attributesTable];
    attributesTableCopy[index] = {
      ...attributesTableCopy[index],
      attributeValue: !attributesTableCopy[index].attributeValue,
    };
    return {
      ...state,
      attributesTable: attributesTableCopy,
    };
  }),
  on(DeactivateStoreChanged, (state) => ({
    ...state,
    deactivateStore: !state.deactivateStore,
  })),
  on(AddressLineChanged, (state, { addressLine }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      addressLine,
    },
  })),
  on(TownChanged, (state, { town }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      town,
    },
  })),
  on(CountryChanged, (state, { country, countryCode }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      country,
      countryCode,
      region: 0,
    },
  })),
  on(CoordinatesChanged, (state, { geoPoint }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      geoPoint,
    },
  })),
  on(PostCodeChanged, (state, { postCode }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      postCode,
    },
  })),
  on(RegionChanged, (state, { region }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      region,
    },
  })),
  on(RegionChanged, (state, { region }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      region,
    },
  })),
  on(RegionValuesChanged, (state, { regionValues }) => ({
    ...state,
    addressDetails: {
      ...state.addressDetails,
      regionValues,
    },
  })),
  on(LanguagesChanged, (state, { languages }) => ({
    ...state,
    languages: languages.map((language) => ({
      ...language,
      view: state.isNew
        ? true
        : !!state.storeNames.find((store) => store.languageCode === language.languageCode),
    })),
    selectedLanguage: languages[0]?.languageCode,
    storeNames: state.isNew
      ? languages.map((language) => ({
          languageCode: language.languageCode,
          name: '',
        }))
      : state.storeNames,
  })),
  on(DisplayLanguageChanged, (state, { languageCode }) => {
    const updatedLanguages = state.languages.map((language) =>
      language.languageCode === languageCode ? { ...language, view: !language.view } : language,
    );
    const visibleLanguages = updatedLanguages.filter((language) => language.view);
    const toggledLang = updatedLanguages.find((language) => language.languageCode === languageCode);

    let selectedLanguage = state.selectedLanguage;
    if (!toggledLang?.view && selectedLanguage === languageCode) {
      selectedLanguage = visibleLanguages[0]?.languageCode || '';
    } else if (toggledLang?.view && visibleLanguages.length === 1) {
      selectedLanguage = languageCode;
    }

    const newStoreNames = visibleLanguages.map((language) => {
      return (
        state.storeNames.find((store) => store.languageCode === language.languageCode) ?? {
          languageCode: language.languageCode,
          name: '',
        }
      );
    });

    return {
      ...state,
      languages: updatedLanguages,
      selectedLanguage,
      storeNames: newStoreNames,
    };
  }),
  on(SaveStoreGroups, (state, { storeGroups }) => ({
    ...state,
    storeGroups,
  })),
  on(SaveClubs, (state, { clubs }) => ({
    ...state,
    clubs,
  })),
  on(SaveBrands, (state, { brands }) => ({
    ...state,
    brands,
  })),
  on(SaveCouponSets, (state, { couponSets }) => ({
    ...state,
    couponSets,
  })),
  on(SaveUsers, (state, { users }) => ({
    ...state,
    users,
  })),
  on(RemoveStoreGroups, (state) => ({
    ...state,
    storeGroups: [],
  })),
  on(RemoveClubs, (state) => ({
    ...state,
    clubs: [],
  })),
  on(RemoveStoreBrands, (state) => ({
    ...state,
    brands: [],
  })),
  on(RemoveCouponsets, (state) => ({
    ...state,
    couponSets: [],
  })),
  on(RemoveUsers, (state) => ({
    ...state,
    users: [],
  })),
  on(RemoveStoreGroup, (state, { storeGroup }) => ({
    ...state,
    storeGroups: state.storeGroups.filter(
      (existingStoreGroup: any) => existingStoreGroup.code !== storeGroup.code,
    ),
  })),
  on(RemoveClub, (state, { club }) => ({
    ...state,
    clubs: state.clubs.filter((existingClub: any) => existingClub.clubCode !== club.clubCode),
  })),
  on(RemoveStoreBrand, (state, { brand }) => ({
    ...state,
    brands: state.brands.filter(
      (existingBrand: any) => existingBrand.brandCode !== brand.brandCode,
    ),
  })),
  on(RemoveCouponset, (state, { couponSet }) => ({
    ...state,
    couponSets: state.couponSets.filter(
      (existingCouponSet: any) => existingCouponSet.code !== couponSet.code,
    ),
  })),
  on(RemoveUser, (state, { user }) => ({
    ...state,
    users: state.users.filter((existingUser: any) => existingUser.userName !== user.userName),
  })),
  on(UpdateStore, (state) => ({
    ...state,
    submit: true,
  })),
  on(SetMerchantID, (state, { merchantID }) => ({
    ...state,
    fromMerchant: true,
    merchant: {
      ...state.merchant,
      merchantID,
    },
  })),
  on(SearchMerchantByIDSuccess, (state, { res }) => ({
    ...state,
    fromMerchant: true,
    merchant: {
      ...state.merchant,
      merchantName: res.payload.data.companyName,
    },
  })),
  on(ActiveComponentChanged, () => initialState),
  on(ResetStateFromRouter, () => ({
    ...initialState
  }))
);
